import { Button, Image, Label, Typography } from '@musinsa/mds';
import type { ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

/**
 * 개별 상품 카드 — `Image` / `Info` / `ImageInfoRow` / `Action` compound.
 *
 * 카테고리 그룹 단위(ProductCardGroup)·섹션 타이틀(ProductCardSectionTitle)·그룹화 helper
 * (groupProductCards)는 `./ProductCardGroup` 으로 분리되어 있다.
 *
 * ```tsx
 * <ProductCard soldOut={p.soldOut}>
 *   <ProductCard.ImageInfoRow>
 *     <ProductCard.Image src={p.imageUrl} alt={p.name} soldOut={p.soldOut} />
 *     <ProductCard.Info brand={p.brand} name={p.name} price={p.price} soldOut={p.soldOut} />
 *   </ProductCard.ImageInfoRow>
 *   {p.actionLabel && <ProductCard.Action onClick={...}>{p.actionLabel}</ProductCard.Action>}
 * </ProductCard>
 * ```
 */

interface ImageBlockProps {
  src: string;
  alt: string;
  /** soldOut 시 dim overlay + "품절" 라벨 좌하단 — F8-1/F8-2 */
  soldOut?: boolean;
  /** 썸네일 비율 — 29CM=square(5:5, 72x72) / 무신사=portrait(세로, 72x86). 서비스별 상이. (SID-4424) */
  imageRatio?: 'square' | 'portrait';
  /** 썸네일 클릭 시 동작 — PDP 이동 등. 미제공 시 비클릭(평문 이미지). (QAT-39446) */
  onClick?: () => void;
  className?: string;
}

const ImageBlock = ({
  src,
  alt,
  soldOut,
  imageRatio = 'portrait',
  onClick,
  className,
}: ImageBlockProps) => {
  const content = (
    <>
      <Image src={src} alt={alt ?? ''} />
      {soldOut && (
        // mds Image 가 wrapper 를 `relative z-5` 로 렌더하므로 dim/품절 라벨을 z-10(>5)로 올린다.
        // 안 그러면 라벨이 이미지에 가려져 미노출된다. (QAT-39520)
        <>
          <div className="absolute inset-0 z-10 bg-black/20" />
          <div className="absolute bottom-[4px] left-[4px] z-10">
            <Label variant="darkGray">품절</Label>
          </div>
        </>
      )}
    </>
  );

  const baseClassName = twMerge(
    // 썸네일 비율 — 29CM 5:5(72x72) / 무신사 세로(72x86). (SID-4424)
    'relative w-[72px] shrink-0 overflow-hidden rounded-[4px]',
    imageRatio === 'square' ? 'h-[72px]' : 'h-[86px]',
    className,
  );

  // onClick 없으면 기존처럼 평문 div. 있으면 button 으로 노출해 키보드/스크린리더 접근성 확보.
  if (!onClick) {
    return <div className={baseClassName}>{content}</div>;
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`${alt} 상품 상세 보기`}
      className={twMerge(baseClassName, 'cursor-pointer')}
    >
      {content}
    </button>
  );
};

interface InfoProps {
  brand: string;
  name: string;
  selectedOption?: string;
  price: number;
  soldOut?: boolean;
  /** 브랜드명 클릭 — 브랜드 스토어 홈 진입. 미제공 시 평문(비클릭). (QAT-39448) */
  onBrandClick?: () => void;
  className?: string;
}

const Info = ({
  brand,
  name,
  selectedOption,
  price,
  soldOut,
  onBrandClick,
  className,
}: InfoProps) => {
  const brandText = (
    <Typography
      variant="body13pxSemi"
      colorScheme={soldOut ? 'gray-600' : 'black'}
      className="truncate"
    >
      {brand}
    </Typography>
  );
  return (
    <div className={twMerge('flex min-w-0 flex-1 flex-col gap-[2px]! justify-center', className)}>
      {onBrandClick ? (
        <button type="button" onClick={onBrandClick} className="block max-w-full min-w-0 text-left">
          {brandText}
        </button>
      ) : (
        brandText
      )}
      <Typography
        variant="body13pxReg"
        colorScheme={soldOut ? 'gray-600' : 'black'}
        className="truncate"
      >
        {name}
      </Typography>
      {selectedOption && (
        <Typography variant="body13pxReg" colorScheme={soldOut ? 'gray-600' : 'blue'}>
          {selectedOption}
        </Typography>
      )}
      <Typography variant="body13pxSemi" colorScheme={soldOut ? 'gray-400' : 'black'}>
        {price.toLocaleString()}원
      </Typography>
    </div>
  );
};

interface ImageInfoRowProps {
  children: ReactNode;
  className?: string;
}

/** Image + Info 묶음 행 (옵션). 직접 children 으로 조합 가능. */
const ImageInfoRow = ({ children, className }: ImageInfoRowProps) => (
  <div className={twMerge('flex items-center gap-[12px]!', className)}>{children}</div>
);

interface ActionProps {
  children: ReactNode;
  onClick?: () => void;
  className?: string;
}

const Action = ({ children, onClick, className }: ActionProps) => (
  // 버튼 height 36px. (SID-4137) 상품 카드 ~ 옵션 선택 영역 사이 12px. (피드백 항목4)
  <Typography variant="body13pxMed" asChild>
    <Button
      variant="secondary"
      className={twMerge('w-full h-[36px] mt-[12px]!', className)}
      onClick={onClick}
    >
      {children}
    </Button>
  </Typography>
);

interface ProductCardRootProps {
  children: ReactNode;
  /** 카드 전체 dim 처리 (자식들이 각자 soldOut prop 도 처리). */
  soldOut?: boolean;
  className?: string;
}

const ProductCardRoot = ({ children, className }: ProductCardRootProps) => (
  <div className={twMerge('flex flex-col', className)}>{children}</div>
);

/** 개별 상품 카드 단위. */
export const ProductCard = Object.assign(ProductCardRoot, {
  Image: ImageBlock,
  Info,
  ImageInfoRow,
  Action,
});
